<template>
  <div class="p-6">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-sm font-medium text-gray-700">
        {{ entries.length }} {{ showInactive ? '' : 'active ' }}entr{{ entries.length !== 1 ? 'ies' : 'y' }}
      </h3>
      <div class="flex items-center gap-4">
        <label class="flex items-center gap-1.5 text-xs text-gray-600">
          <input v-model="showInactive" type="checkbox" class="rounded border-gray-300" @change="fetchEntries" />
          Show inactive
        </label>
        <button
          class="text-xs text-gray-500 hover:text-gray-700"
          :disabled="loading"
          @click="fetchEntries"
        >
          <font-awesome-icon :icon="['fas', 'arrows-rotate']" class="mr-1" />
          Refresh
        </button>
        <button v-if="!formOpen" class="btn-primary-sm" @click="openCreate">
          <font-awesome-icon :icon="['fas', 'plus']" class="mr-1" />
          Add entry
        </button>
      </div>
    </div>

    <div v-if="formOpen" class="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
      <p class="text-sm font-medium text-gray-800 mb-3">{{ editingEntry ? 'Edit entry' : 'New entry' }}</p>
      <div v-if="formError" class="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
        {{ formError }}
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label for="phone-book-name" class="block text-xs text-gray-600 mb-1">Name</label>
          <input
            id="phone-book-name"
            v-model="form.name"
            type="text"
            data-spoof-kind="practice" class="sensitive w-full border rounded px-2 py-1.5 text-sm"
            :class="fieldErrors.name ? 'border-red-400' : 'border-gray-300'"
          />
          <p v-for="message in fieldErrors.name" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
        </div>
        <div>
          <label for="phone-book-phone" class="block text-xs text-gray-600 mb-1">Phone number</label>
          <input
            id="phone-book-phone"
            v-model="form.phone_number"
            v-maska="{ mask: '(###) ###-####' }"
            type="tel"
            placeholder="(555) 123-4567"
            data-spoof-kind="phone" class="sensitive w-full border rounded px-2 py-1.5 text-sm"
            :class="fieldErrors.phone_number ? 'border-red-400' : 'border-gray-300'"
          />
          <p v-for="message in fieldErrors.phone_number" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
        </div>
        <div>
          <label for="phone-book-role" class="block text-xs text-gray-600 mb-1">Role</label>
          <select
            id="phone-book-role"
            v-model="form.role_id"
            class="w-full border rounded px-2 py-1.5 text-sm bg-white"
            :class="fieldErrors.role_id ? 'border-red-400' : 'border-gray-300'"
          >
            <option :value="null" disabled>Select role...</option>
            <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.display_name }}</option>
          </select>
          <p v-for="message in fieldErrors.role_id" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
        </div>
        <div class="md:col-span-3">
          <label for="phone-book-notes" class="block text-xs text-gray-600 mb-1">Notes <span class="text-gray-400">(optional)</span></label>
          <textarea
            id="phone-book-notes"
            v-model="form.notes"
            rows="2"
            placeholder="Hours, who to ask for, fax first..."
            class="sensitive w-full border rounded px-2 py-1.5 text-sm"
            :class="fieldErrors.notes ? 'border-red-400' : 'border-gray-300'"
            data-demo-blur
          />
          <p v-for="message in fieldErrors.notes" :key="message" class="mt-1 text-xs text-red-600">{{ message }}</p>
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-4">
        <button class="btn-secondary-sm" :disabled="saving" @click="closeForm">Cancel</button>
        <button class="btn-primary-sm" :disabled="!canSave" @click="save">
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'circle-notch']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <div v-else-if="entries.length === 0" class="py-12 text-center">
      <font-awesome-icon :icon="['fas', 'phone']" class="text-3xl text-gray-300 mb-3" />
      <p class="text-sm text-gray-500">No phone book entries</p>
    </div>

    <div v-else class="border border-gray-200 rounded-lg overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-gray-50 text-xs text-gray-500 uppercase">
          <tr>
            <th class="px-4 py-2 text-left font-medium">Name</th>
            <th class="px-4 py-2 text-left font-medium">Role</th>
            <th class="px-4 py-2 text-left font-medium">Phone number</th>
            <th class="px-4 py-2 text-left font-medium">Notes</th>
            <th class="px-4 py-2 text-left font-medium">Status</th>
            <th class="px-4 py-2" />
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="entry in entries" :key="entry.id" data-test="phone-book-row" class="align-top">
            <td data-spoof-kind="practice" class="sensitive px-4 py-2.5 font-medium text-gray-900">{{ entry.name }}</td>
            <td class="px-4 py-2.5 text-gray-700">{{ entry.role.display_name }}</td>
            <td data-spoof-kind="phone" class="sensitive px-4 py-2.5 text-gray-700 whitespace-nowrap">{{ formatPhone(entry.phone_number) }}</td>
            <td class="px-4 py-2.5 text-gray-600 max-w-xs">
              <DemoText v-if="entry.notes" tag="span" class="whitespace-pre-line" :text="entry.notes" />
              <span v-else class="text-gray-300">--</span>
            </td>
            <td class="px-4 py-2.5">
              <span
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="entry.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'"
              >{{ entry.is_active ? 'active' : 'inactive' }}</span>
            </td>
            <td class="px-4 py-2.5 text-right whitespace-nowrap">
              <button class="text-xs text-blue-600 hover:text-blue-800 mr-3" @click="openEdit(entry)">Edit</button>
              <button
                class="text-xs"
                :class="entry.is_active ? 'text-red-600 hover:text-red-800' : 'text-green-700 hover:text-green-900'"
                @click="setActive(entry, !entry.is_active)"
              >{{ entry.is_active ? 'Deactivate' : 'Reactivate' }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { vMaska } from 'maska/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DemoText from '@/components/DemoMode/DemoText.vue'
import {
  listPhoneBookEntries,
  createPhoneBookEntry,
  updatePhoneBookEntry,
} from '@/services/api/telehealthPhoneBook'
import { listParticipantRoles } from '@/services/api/voiceApi'
import { parseTelehealthError } from '@/services/api/telehealthErrors'

const EMPTY_FORM = { name: '', phone_number: '', role_id: null, notes: '' }

export default {
  name: 'TelephonyPhoneBook_Admin',
  components: { FontAwesomeIcon, DemoText },
  directives: { maska: vMaska },

  data() {
    return {
      entries: [],
      roles: [],
      loading: true,
      error: null,
      showInactive: false,
      formOpen: false,
      editingEntry: null,
      form: { ...EMPTY_FORM },
      fieldErrors: {},
      formError: null,
      saving: false,
    }
  },

  computed: {
    canSave() {
      return !this.saving && !!this.form.name.trim() && !!this.form.phone_number.trim() && !!this.form.role_id
    },
  },

  mounted() {
    this.fetchEntries()
    this.fetchRoles()
  },

  methods: {
    async fetchEntries() {
      this.loading = true
      this.error = null
      try {
        this.entries = await listPhoneBookEntries(this.showInactive ? undefined : { is_active: true })
      } catch {
        this.error = 'Failed to load phone book'
      } finally {
        this.loading = false
      }
    },

    async fetchRoles() {
      try {
        this.roles = await listParticipantRoles({ is_system: false })
      } catch {
        this.error = 'Failed to load participant roles'
      }
    },

    openCreate() {
      this.editingEntry = null
      this.form = { ...EMPTY_FORM }
      this.resetFormErrors()
      this.formOpen = true
    },

    openEdit(entry) {
      this.editingEntry = entry
      this.form = { name: entry.name, phone_number: entry.phone_number, role_id: entry.role.id, notes: entry.notes }
      this.resetFormErrors()
      this.formOpen = true
    },

    closeForm() {
      this.formOpen = false
      this.editingEntry = null
      this.form = { ...EMPTY_FORM }
      this.resetFormErrors()
    },

    resetFormErrors() {
      this.fieldErrors = {}
      this.formError = null
    },

    async save() {
      if (!this.canSave) return
      this.saving = true
      this.resetFormErrors()
      const body = {
        name: this.form.name.trim(),
        phone_number: `+1${this.form.phone_number.replace(/\D/g, '')}`,
        role_id: this.form.role_id,
        notes: this.form.notes.trim(),
      }
      try {
        if (this.editingEntry) {
          await updatePhoneBookEntry(this.editingEntry.id, body)
        } else {
          await createPhoneBookEntry(body)
        }
        this.closeForm()
        await this.fetchEntries()
      } catch (err) {
        this.applyErrors(err, 'Failed to save entry')
      } finally {
        this.saving = false
      }
    },

    async setActive(entry, isActive) {
      this.error = null
      try {
        await updatePhoneBookEntry(entry.id, { is_active: isActive })
        await this.fetchEntries()
      } catch (err) {
        this.error = this.errorMessage(err, isActive ? 'Failed to reactivate entry' : 'Failed to deactivate entry')
      }
    },

    applyErrors(err, fallback) {
      const { message, fieldErrors } = parseTelehealthError(err, fallback)
      this.fieldErrors = fieldErrors
      this.formError = message || (Object.keys(fieldErrors).length ? null : fallback)
    },

    errorMessage(err, fallback) {
      return parseTelehealthError(err, fallback).summary
    },

    formatPhone(phone) {
      if (!phone) return ''
      const digits = phone.replace(/\D/g, '')
      if (digits.length === 11 && digits[0] === '1') {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
      }
      if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
      }
      return phone
    },
  },
}
</script>
